import React from 'react';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import CapsuleIcon from 'new/components/icons/CapsuleIcon';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CopyIconButton } from 'new/elements/Button/CopyIconButton';
import { parseLifecycleStatus } from 'new/modules/settings/capsules/domain/capsule.status';
import type { ClusterOption } from './utils/clusterOptions';
import styles from './RuntimeSelection.module.css';

/** Secondary text rendered next to the badge in the description row.
 *  For Nexla Cloud cards (isDefault) the cluster name is returned;
 *  for capsule cards the trimmed description is used. Returns null
 *  when no secondary text should render. */
export function getRuntimeCardSecondaryText(option: ClusterOption): string | null {
  if (option.isDefault) {
    const name = option.name?.trim();
    return name || null;
  }
  const raw = option.description;
  if (raw == null || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function OptionIcon({ option }: { option: ClusterOption }) {
  if (option.isDefault) {
    return (
      <div className={styles.iconWrapper}>
        <CloudOutlinedIcon fontSize="small" className={styles.optionIconCloud} />
      </div>
    );
  }
  const lifecycle = parseLifecycleStatus(option.status);
  if (lifecycle === 'active') {
    return (
      <div className={styles.iconWrapper}>
        <CapsuleIcon fontSize="small" className={styles.optionIconCapsuleActive} />
      </div>
    );
  }
  return (
    <div className={styles.iconWrapper}>
      <WarningAmberIcon fontSize="small" className={styles.optionIconWarning} />
    </div>
  );
}

function getStatusLabel(option: ClusterOption) {
  const lifecycle = parseLifecycleStatus(option.status);
  const status = lifecycle ?? option.status;
  return status ? status.toUpperCase() : null;
}

export function RuntimeOptionContent({ option }: { option: ClusterOption }) {
  const secondary = getRuntimeCardSecondaryText(option);
  const statusLabel = getStatusLabel(option);

  return (
    <div className={styles.runtimeItem}>
      <OptionIcon option={option} />
      <div className={styles.runtimeItemContent}>
        <span className={styles.runtimeItemTitle}>{option.label}</span>
        {secondary ? <span className={styles.runtimeItemDescription}>{secondary}</span> : null}
        {(option.uid || statusLabel) && (
          <div className={styles.runtimeItemMeta}>
            {option.uid ? (
              <span className={styles.uidGroup}>
                <span className={styles.uidText}>UID: {option.uid}</span>
                <CopyIconButton
                  text={option.uid}
                  title={`Copy Dataplane UID ${option.uid}`}
                  className={styles.uidCopyButton}
                  copiedClassName={styles.uidCopyButtonCopied}
                />
              </span>
            ) : null}
            {statusLabel ? <span className={styles.statusBadge}>{statusLabel}</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}
